import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CloudOff, Loader2, Swords } from "lucide-react";
import AdminDesk from "../components/AdminDesk";
import AdminFloorGrid from "../components/AdminFloorGrid";
import AdminP2PChrome from "../p2p/AdminP2PChrome";
import AdminP2PFiltersRail from "../p2p/AdminP2PFiltersRail";
import P2PFloorIndex from "../p2p/floor/P2PFloorIndex";
import P2PFloorDetail from "../p2p/floor/P2PFloorDetail";
import { fetchAdminP2PDesk } from "../p2p/api/fetchAdminP2P";
import { clientFilterDuels, sortDuels } from "../p2p/constants";
import "../styles/admin-markets.css";
import "../styles/admin-markets-floor.css";
import "../p2p/admin-p2p.css";

const PAGE_SIZE = 15;

export default function P2PPage() {
  const [desk, setDesk] = useState("all");
  const [status, setStatus] = useState("All");
  const [sortKey, setSortKey] = useState("newest");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [duels, setDuels] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 320);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(
    async ({ pageNum = 1, refresh = false } = {}) => {
      setLoading(true);
      if (refresh) setRefreshing(true);
      setError(null);

      try {
        const data = await fetchAdminP2PDesk({
          page: pageNum,
          limit: PAGE_SIZE,
          status: status === "All" ? "all" : status,
          desk,
          search: debouncedSearch,
          refresh,
        });

        setDuels(data?.duels || []);
        setSummary(data?.summary || null);
        setPagination((prev) => ({
          ...prev,
          ...(data?.pagination || {}),
          limit: data?.pagination?.limit || PAGE_SIZE,
        }));
      } catch (err) {
        setDuels([]);
        setError(err.response?.data?.error || err.message || "Failed to load P2P desk");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [desk, status, debouncedSearch]
  );

  const fetchPage = useCallback(
    (pageNum, refresh = false) => {
      setPage(pageNum);
      load({ pageNum, refresh });
    },
    [load]
  );

  useEffect(() => {
    fetchPage(1);
  }, [desk, status, debouncedSearch, fetchPage]);

  const handleRefresh = () => load({ pageNum: page, refresh: true });

  const handlePageChange = (nextPage) => {
    if (nextPage === page || nextPage < 1) return;
    fetchPage(nextPage);
  };

  const displayDuels = useMemo(() => {
    let list = clientFilterDuels(duels, search);
    return sortDuels(list, sortKey);
  }, [duels, search, sortKey]);

  const selectedDuel = useMemo(() => {
    if (!selectedId) return null;
    return displayDuels.find((d) => d._id === selectedId) || duels.find((d) => d._id === selectedId) || null;
  }, [displayDuels, duels, selectedId]);

  useEffect(() => {
    if (!displayDuels.length) {
      setSelectedId(null);
      return;
    }
    const stillVisible = selectedId && displayDuels.some((d) => d._id === selectedId);
    if (!stillVisible) {
      setSelectedId(displayDuels[0]._id);
    }
  }, [displayDuels, selectedId]);

  const activeFilterCount = [
    desk !== "all",
    status !== "All",
    sortKey !== "newest",
  ].filter(Boolean).length;

  const handleClearFilters = () => {
    setDesk("all");
    setStatus("All");
    setSortKey("newest");
    setSearch("");
  };

  const railProps = {
    desk,
    status,
    sortKey,
    summary,
    resultCount: displayDuels.length,
    onDeskChange: setDesk,
    onStatusChange: setStatus,
    onSortChange: setSortKey,
    onClear: handleClearFilters,
    activeCount: activeFilterCount,
  };

  const isLoading = loading && !refreshing && duels.length === 0;

  return (
    <div className="am-page ap2p-page">
      <div className="am-aurora" aria-hidden>
        <span className="am-aurora-a ap2p-aurora" />
      </div>

      <AdminP2PChrome
        search={search}
        onSearchChange={setSearch}
        loading={loading || refreshing}
        onRefresh={handleRefresh}
        filtersOpen={filtersOpen}
        onFiltersToggle={() => setFiltersOpen((v) => !v)}
        activeFilterCount={activeFilterCount}
        summary={summary}
        totalLoaded={pagination.total || duels.length}
      />

      <AdminDesk sectionKey="p2p" sidebarLabel="P2P filters" sidebar={<AdminP2PFiltersRail {...railProps} />}>
            {error ? (
              <div className="ap2p-error-banner" role="alert">
                <span>{error}</span>
                <button type="button" onClick={() => setError(null)}>
                  Dismiss
                </button>
              </div>
            ) : null}

            <div className="am-board am-board-floor">
              {isLoading ? (
                <div className="am-state">
                  <Loader2 size={28} className="am-spin" />
                  <p>Loading P2P duels…</p>
                </div>
              ) : error && !duels.length ? (
                <div className="am-state">
                  <CloudOff size={28} />
                  <p>{error}</p>
                  <button type="button" className="am-chrome-btn am-chrome-btn-accent" onClick={handleRefresh}>
                    Try again
                  </button>
                </div>
              ) : displayDuels.length === 0 && !loading ? (
                <div className="am-state">
                  <Swords size={28} />
                  <h2>No duels match</h2>
                  <p>
                    {desk === "needs_outcome"
                      ? "Nothing in the outcome queue — all caught up."
                      : "Try clearing filters or adjusting your search."}
                  </p>
                  {activeFilterCount || search ? (
                    <button type="button" className="am-chrome-btn am-chrome-btn-accent" onClick={handleClearFilters}>
                      Clear filters
                    </button>
                  ) : null}
                </div>
              ) : (
                <AdminFloorGrid
                  sectionKey="p2p"
                  index={
                    <P2PFloorIndex
                      duels={displayDuels}
                      selectedId={selectedId}
                      onSelect={setSelectedId}
                      desk={desk}
                      pagination={pagination}
                      loading={loading}
                      onPageChange={handlePageChange}
                    />
                  }
                  detail={<P2PFloorDetail duel={selectedDuel} />}
                />
              )}
            </div>
      </AdminDesk>

      {filtersOpen ? (
        <div className="am-filters-backdrop" onClick={() => setFiltersOpen(false)} role="presentation">
          <div className="am-filters-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="P2P filters">
            <AdminP2PFiltersRail {...railProps} compact onClose={() => setFiltersOpen(false)} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
